import { Suspense, useContext } from "react";
import { TableSkeleton } from "../../components/Skeleton/TableSkeleton";
import { DataTable } from "../../components/ui/data-table";
import {
  capitalizeWords,
  formatToShortDate,
  formatUniversalNumber,
} from "../../lib/utils";
import { PaymentContext } from "../../providers/PaymentProvider";

const PaymentTable = () => {
  const columns = [
    {
      accessorKey: "id",
      header: "",
      cell: ({ row }: any) => {
        const status = row.original.isCancelled ? "Refund" : "Paid";
        const badgeStyles: { Paid: string; Refund: string } = {
          Paid: "bg-[#3AB867]",
          Refund: "bg-[#D54841]",
        };
        return (
          <div className="flex items-center justify-center space-x-2">
            <span className={`h-2 w-2 rounded-full ${badgeStyles[status]}`} />
          </div>
        );
      },
      enableSorting: false,
      size: 40,
    },
    {
      accessorKey: "externalId",
      header: "Payment ID",
      cell: ({ row }: any) => {
        return (
          <span className="">
            {row.original.externalId.replace(/(\s.*)/, "")}
          </span>
        );
      }, // Updated to match the interface
    },
    {
      accessorKey: "paymentDate",
      header: "Date",
      cell: ({ row }: any) => formatToShortDate(row.original.paymentDate), // Updated to match the interface
    },
    {
      accessorKey: "customerName",
      header: "Client Name",
      cell: ({ row }: any) => {
        const customerName = row.original.customerName || "-"; // Show "-" if value is empty

        // Capitalize each word in company name
        const capitalizedCustomerName = capitalizeWords(customerName);

        return <span>{capitalizedCustomerName}</span>;
      },
    },
    {
      accessorKey: "referenceNumber",
      header: "Invoice Number",
      cell: ({ row }: any) => {
        const value = row.original.referenceNumber;
        return value && value.toString().trim() !== "" ? value : "-";
      },
    },
    {
      accessorKey: "totalAmt",
      header: "Amount Paid",
      size: 80,
      minSize: 80,
      maxSize: 80,
      enableResizing: false,
      cell: ({ row }: any) => {
        const amount: number = row.original.totalAmt;
        return (
          <span style={{ width: "80px" }} className="text-right block">
            {formatUniversalNumber(amount)}
          </span>
        );
      },
    },
    {
      accessorKey: "methodName",
      header: "Payment Method",
      cell: ({ row }: any) => row.original.methodName || "-", // Updated to match the interface
    },
    {
      accessorKey: "isCancelled",
      header: "Type",
      cell: ({ row }: any) => {
        const status = row.original.isCancelled ? "Refund" : "Paid"; // Updated to match the interface
        const badgeStyles: any = {
          Paid: "BookEZ_bg_green_light BookEZ_font_color_green",
          Refund: "bg-amber-100 BookEZ_font_color_yellow",
        };

        return (
          <div className="space-x-2">
            <span
              className={`BookEZ_font_body_size  BookEZ_font_body_medium px-3 py-1 rounded-full ${badgeStyles[status]}`}
            >
              {status}
            </span>
          </div>
        );
      },
    },
  ];

  const {
    paymentData,
    isLoading,
    pageSize,
    pageNumber,
    totalPages,
    totalRecords,
    handlePageChange,
    handlePageSizeChange,
  } = useContext(PaymentContext);

  return (
    <div>
      {isLoading ? (
        <TableSkeleton />
      ) : (
        <Suspense fallback={<TableSkeleton />}>
          <DataTable
            columns={columns}
            data={paymentData}
            showPagination={true}
            pageSize={pageSize}
            currentPage={pageNumber}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            totalRecords={totalRecords}
          />
        </Suspense>
      )}
    </div>
  );
};

export default PaymentTable;
